import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router-dom";
import { toast } from "sonner";

import FastAPI from "../../api/FastAPI";

import type { UseMutationResult } from "@tanstack/react-query";

import type {
  GetTimeslotResponse,
  UpdatedTimeslot,
} from "../../api/FastAPI.types";
import type { FASTChannelRoutesParams } from "../../components/routes/FASTChannelRoutes";

interface UpdateTimeslotVariables {
  isNewTimeslot: boolean;
  updatedTimeslot: UpdatedTimeslot;
}

interface UseFASTTimeslotMutationsParams {
  updateFASTTimeslot: UseMutationResult<
    GetTimeslotResponse,
    unknown,
    UpdateTimeslotVariables,
    GetTimeslotResponse
  >;
}
export default function useFASTTimeslotMutations(): UseFASTTimeslotMutationsParams {
  const history = useHistory();
  const { t } = useTranslation("editor-fast-channel");

  const { channelId, timeslotId } = useParams<
    FASTChannelRoutesParams & {
      timeslotId: string;
    }
  >();
  const queryClient = useQueryClient();

  const getTimeslotQuery = FastAPI.generateQuery({
    fn: FastAPI.getTimeslot,
    params: [
      {
        channelId,
        timeslotId,
      },
    ],
  });

  const getTimeslotListQuery = FastAPI.generateQuery({
    fn: FastAPI.getTimeslotList,
    params: [channelId],
  });

  const updateFASTTimeslot = useMutation({
    mutationFn: async ({
      updatedTimeslot,
      isNewTimeslot,
    }: UpdateTimeslotVariables) =>
      isNewTimeslot
        ? FastAPI.createTimeslot({
            channelId,
            data: updatedTimeslot,
          })
        : FastAPI.updateTimeslot({
            channelId,
            timeslotId,
            data: updatedTimeslot,
          }),
    onMutate: ({
      updatedTimeslot,
      isNewTimeslot,
    }): GetTimeslotResponse | undefined => {
      if (isNewTimeslot) return undefined;

      queryClient.cancelQueries(getTimeslotQuery.queryKey);

      const previousTimeslot = queryClient.getQueryData<GetTimeslotResponse>(
        getTimeslotQuery.queryKey,
      );

      queryClient.setQueryData(getTimeslotQuery.queryKey, {
        ...previousTimeslot,
        ...updatedTimeslot,
      });

      return previousTimeslot;
    },
    onSuccess: ({ uuid }, { isNewTimeslot }): void => {
      if (isNewTimeslot) {
        history.replace(uuid);
      }

      queryClient.invalidateQueries(getTimeslotListQuery.queryKey);

      toast.success(
        isNewTimeslot ? t("timeslotCreateSuccess") : t("timeslotUpdateSuccess"),
      );
    },
    onError: (_, { isNewTimeslot }): void => {
      toast.error(
        isNewTimeslot ? t("timeslotCreateError") : t("timeslotUpdateError"),
      );
    },
  });

  return {
    updateFASTTimeslot,
  };
}
